// src/components/BridgeWidget.js

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { message, Spin } from "antd";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { ethers } from "ethers";
import { CHAINS_CONFIG } from "../chains";
import BaitLogo from "../BaitLogo.png";
import { ArrowUpDown, ExternalLink } from "lucide-react";

// =======================================================
// CONTRACT ADDRESSES
// =======================================================

const POLYGON_BRIDGE =
  "0xb6C330639Fb62dFCb4C1c202d5A7A6d6906d4c05";

const GOLDCHAIN_BRIDGE =
  "0xbEC9BAd163095CaC3bDA082A4ce65e5C65d46e21";

const USDT_POLYGON =
  "0xc2132D05D31c914a87C6611C10748AEb04B58e8F";

const WUSDT_GOLDCHAIN =
  "0x4791394e566020D7a69A6029D4FD1324B9706107";

// =======================================================
// CONTRACT ABIs
// =======================================================

const BRIDGE_ABI = [
  "function getUserBalance(address) view returns (uint256)",
  "function initiateCrossChainWithdrawal(uint256 destChainId, uint256 amount, address destAddress) external payable",
  "function addLiquidity(uint256 amount) external",
  "function withdraw(uint256 amount) external",
];

const ERC20_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function allowance(address,address) view returns (uint256)",
  "function approve(address,uint256) returns (bool)",
];

// =======================================================
// BRIDGE ACTIVITY STORAGE
// =======================================================

const BRIDGE_ACTIVITY_KEY = "baitpay:bridgeActivity";
const BRIDGE_ACTIVITY_LIMIT = 50;

const readBridgeActivity = () => {
  try {
    const raw = localStorage.getItem(BRIDGE_ACTIVITY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeBridgeActivity = (list) => {
  try {
    const trimmed = list.slice(0, BRIDGE_ACTIVITY_LIMIT);
    localStorage.setItem(
      BRIDGE_ACTIVITY_KEY,
      JSON.stringify(trimmed)
    );
  } catch (err) {
    console.error(
      "Failed to persist bridge activity:",
      err
    );
  }
};

const addBridgeActivityEntry = (entry) => {
  const list = readBridgeActivity();
  const next = [entry, ...list].slice(
    0,
    BRIDGE_ACTIVITY_LIMIT
  );
  writeBridgeActivity(next);
  return next;
};

const updateBridgeActivityEntry = (id, patch) => {
  const list = readBridgeActivity();
  const next = list.map((e) =>
    e.id === id ? { ...e, ...patch } : e
  );
  writeBridgeActivity(next);
  return next;
};

// =======================================================
// RPC HELPER
// =======================================================

function getRpcUrl(chainName) {
  const lower = chainName.toLowerCase();

  for (const key of Object.keys(CHAINS_CONFIG)) {
    if (
      key.toLowerCase() === lower &&
      CHAINS_CONFIG[key].rpcUrl
    ) {
      return CHAINS_CONFIG[key].rpcUrl;
    }
  }

  if (lower === "polygon") {
    return "https://polygon-bor.publicnode.com";
  }

  if (lower === "goldchain") {
    return "https://rpc.goldchain.tech";
  }

  throw new Error(`No RPC URL for ${chainName}`);
}

function getExplorerUrl(chain, txHash) {
  if (!txHash) return "#";
  if (chain === "polygon") {
    return `https://polygonscan.com/tx/${txHash}`;
  }
  // GoldChain explorer (same one used by WalletView activity)
  return `https://baitcoinexplorer.bait-coin.com/#/transaction/${txHash}`;
}

function formatBridgeDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// =======================================================
// BRIDGE WIDGET
// =======================================================

const BridgeWidget = ({ wallet, seedPhrase }) => {
  const [fromChain, setFromChain] = useState("polygon");
  const [toChain, setToChain] = useState("goldchain");

  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const [walletBalances, setWalletBalances] = useState({
    polygon: 0,
    goldchain: 0,
  });

  const [nativeBalances, setNativeBalances] = useState({
    polygon: 0,
    goldchain: 0,
  });

  // UI indicator while the auto-claim is running
  const [claimingChain, setClaimingChain] = useState(null);

  // Only set AFTER a successful bridge in this session.
  // { chain, token, startedAt, activityId }
  const [awaitingClaim, setAwaitingClaim] = useState(null);

  // Recent bridge activity (filtered by current wallet)
  const [bridgeActivity, setBridgeActivity] = useState([]);

  const [switchHover, setSwitchHover] = useState(false);

  // Synchronous lock
  const claimingRef = useRef(false);

  // Cooldown between actual sends
  const lastSendAtRef = useRef(0);

  // In-flight withdraw tx
  const pendingTxRef = useRef(null); // { chain, hash, rawBal }

  const polygonRpc = getRpcUrl("polygon");
  const goldchainRpc = getRpcUrl("goldchain");

  // =====================================================
  // LOAD BRIDGE ACTIVITY
  // =====================================================

  useEffect(() => {
    if (!wallet) {
      setBridgeActivity([]);
      return;
    }
    const all = readBridgeActivity();
    const mine = all.filter(
      (e) =>
        String(e.wallet || "").toLowerCase() ===
        wallet.toLowerCase()
    );
    setBridgeActivity(mine);
  }, [wallet]);

  // =====================================================
  // LOAD BALANCES
  // =====================================================

  const loadBalances = useCallback(async () => {
    if (!wallet) return;

    try {
      const polygonProvider =
        new ethers.JsonRpcProvider(polygonRpc);

      const goldchainProvider =
        new ethers.JsonRpcProvider(goldchainRpc);

      let polyNative = 0;
      let goldNative = 0;

      try {
        const polyNativeBal =
          await polygonProvider.getBalance(wallet);
        polyNative = Number(
          ethers.formatEther(polyNativeBal)
        );
      } catch (e) {
        console.error("Polygon native balance error:", e);
      }

      try {
        const goldNativeBal =
          await goldchainProvider.getBalance(wallet);
        goldNative = Number(
          ethers.formatEther(goldNativeBal)
        );
      } catch (e) {
        console.error("GoldChain native balance error:", e);
      }

      setNativeBalances({
        polygon: polyNative,
        goldchain: goldNative,
      });

      let polyWallet = 0;
      let goldWallet = 0;

      try {
        const usdt = new ethers.Contract(
          USDT_POLYGON,
          ERC20_ABI,
          polygonProvider
        );
        polyWallet = Number(
          ethers.formatUnits(
            await usdt.balanceOf(wallet),
            6
          )
        );
      } catch (e) {
        console.error("Polygon USDT balance error:", e);
      }

      try {
        const wusdt = new ethers.Contract(
          WUSDT_GOLDCHAIN,
          ERC20_ABI,
          goldchainProvider
        );
        goldWallet = Number(
          ethers.formatUnits(
            await wusdt.balanceOf(wallet),
            6
          )
        );
      } catch (e) {
        console.error("GoldChain WUSDT balance error:", e);
      }

      setWalletBalances({
        polygon: polyWallet,
        goldchain: goldWallet,
      });
    } catch (err) {
      console.error("Balance load error:", err);
    }
  }, [wallet, polygonRpc, goldchainRpc]);

  useEffect(() => {
    if (wallet) {
      loadBalances();
      const interval = setInterval(loadBalances, 15000);
      return () => clearInterval(interval);
    }
  }, [wallet, loadBalances]);

  // =====================================================
  // AUTO-CLAIM
  // =====================================================

  useEffect(() => {
    if (!wallet || !seedPhrase || !awaitingClaim) return;

    const { chain, token, startedAt, activityId } =
      awaitingClaim;

    const rpcUrl =
      chain === "polygon" ? polygonRpc : goldchainRpc;
    const bridgeAddr =
      chain === "polygon"
        ? POLYGON_BRIDGE
        : GOLDCHAIN_BRIDGE;
    const nativeSymbol =
      chain === "polygon" ? "MATIC" : "BAIT";
    const chainLabel =
      chain === "polygon" ? "Polygon" : "GoldChain";

    const provider = new ethers.JsonRpcProvider(rpcUrl);

    let cancelled = false;

    const tryClaim = async () => {
      if (cancelled) return;

      if (claimingRef.current) return;
      claimingRef.current = true;

      try {
        // =================================================
        // PHASE 1: pending withdraw tx
        // =================================================
        const pending = pendingTxRef.current;

        if (pending && pending.chain === chain) {
          let receipt = null;
          try {
            receipt = await provider.getTransactionReceipt(
              pending.hash
            );
          } catch (e) {
            console.warn(
              "[bridge] receipt lookup failed:",
              e
            );
            return;
          }

          if (!receipt) {
            console.log(
              `[bridge] withdraw tx still pending: ${pending.hash}`
            );
            return;
          }

          pendingTxRef.current = null;

          if (receipt.status === 1) {
            const balNum = Number(
              ethers.formatUnits(pending.rawBal, 6)
            );

            message.success({
              content: `Received ${balNum.toFixed(
                6
              )} ${token} on ${chainLabel}!`,
              key: `auto-claim-${chain}`,
            });

            console.log(
              `[bridge] withdraw confirmed: ${pending.hash}`
            );

            // ── Update activity entry to completed
            if (activityId) {
              const updated =
                updateBridgeActivityEntry(activityId, {
                  status: "completed",
                  claimTxHash: pending.hash,
                  completedAt: Date.now(),
                  receivedAmount: balNum.toFixed(6),
                });
              setBridgeActivity(
                updated.filter(
                  (e) =>
                    String(e.wallet).toLowerCase() ===
                    wallet.toLowerCase()
                )
              );
            }

            setClaimingChain(null);
            setAwaitingClaim(null);
            loadBalances();
          } else {
            console.warn(
              `[bridge] withdraw tx reverted: ${pending.hash}`
            );

            // ── Update activity entry to failed
            if (activityId) {
              const updated =
                updateBridgeActivityEntry(activityId, {
                  status: "failed",
                  claimTxHash: pending.hash,
                  failedAt: Date.now(),
                });
              setBridgeActivity(
                updated.filter(
                  (e) =>
                    String(e.wallet).toLowerCase() ===
                    wallet.toLowerCase()
                )
              );
            }

            setClaimingChain(null);
          }

          return;
        }

        // =================================================
        // PHASE 2: no pending tx
        // =================================================

        if (Date.now() - startedAt < 20000) return;

        if (Date.now() - startedAt > 30 * 60 * 1000) {
          console.warn(
            `[bridge] auto-claim timed out on ${chain}`
          );
          setClaimingChain(null);
          setAwaitingClaim(null);
          return;
        }

        if (
          Date.now() - lastSendAtRef.current < 30000 &&
          lastSendAtRef.current !== 0
        ) {
          return;
        }

        const bridgeRead = new ethers.Contract(
          bridgeAddr,
          BRIDGE_ABI,
          provider
        );

        const rawBal =
          await bridgeRead.getUserBalance(wallet);

        const balNum = Number(
          ethers.formatUnits(rawBal, 6)
        );

        console.log(
          `[bridge] ${chain} getUserBalance = ${balNum}`
        );

        if (balNum <= 0) return;

        const nativeBalRaw =
          await provider.getBalance(wallet);
        const nativeBal = Number(
          ethers.formatEther(nativeBalRaw)
        );

        if (nativeBal < 0.001) {
          console.warn(
            `[bridge] not enough ${nativeSymbol} on ${chainLabel} to claim`
          );
          return;
        }

        lastSendAtRef.current = Date.now();

        setClaimingChain(chain);
        message.loading({
          content: `Claiming ${balNum.toFixed(
            6
          )} ${token} from ${chainLabel}...`,
          key: `auto-claim-${chain}`,
          duration: 0,
        });

        const w = ethers.Wallet.fromPhrase(seedPhrase);
        const signer = new ethers.Wallet(
          w.privateKey,
          provider
        );

        const bridgeWrite = new ethers.Contract(
          bridgeAddr,
          BRIDGE_ABI,
          signer
        );

        const feeData = await provider.getFeeData();
        const gasPrice =
          feeData.gasPrice ||
          ethers.parseUnits("100", "gwei");

        const nonce = await provider.getTransactionCount(
          wallet,
          "pending"
        );

        const tx = await bridgeWrite.withdraw(rawBal, {
          gasLimit: 300000,
          gasPrice,
          nonce,
        });

        pendingTxRef.current = {
          chain,
          hash: tx.hash,
          rawBal,
        };

        console.log(
          `[bridge] withdraw broadcast: ${tx.hash}`
        );
      } catch (err) {
        const msg =
          err?.info?.error?.message ||
          err?.shortMessage ||
          err?.message ||
          "";

        if (
          msg.includes("already known") ||
          msg.includes("already imported") ||
          msg.includes("already exists")
        ) {
          console.warn(
            "[bridge] tx already in mempool, waiting for confirmation"
          );
          return;
        }

        console.error(
          `[bridge] withdraw failed on ${chain}:`,
          err
        );

        const reason =
          err?.reason ||
          err?.shortMessage ||
          err?.message ||
          "Withdraw failed";

        message.error({
          content: `Claim failed: ${reason}`,
          key: `auto-claim-${chain}`,
        });

        setClaimingChain(null);
      } finally {
        claimingRef.current = false;
      }
    };

    const firstTimer = setTimeout(tryClaim, 21000);
    const interval = setInterval(tryClaim, 10000);

    return () => {
      cancelled = true;
      clearTimeout(firstTimer);
      clearInterval(interval);
    };
  }, [
    wallet,
    seedPhrase,
    awaitingClaim,
    polygonRpc,
    goldchainRpc,
    loadBalances,
  ]);

  // =====================================================
  // SWITCH CHAINS
  // =====================================================

  const switchChains = () => {
    setFromChain(toChain);
    setToChain(fromChain);
    setAmount("");
  };

  // =====================================================
  // BRIDGE
  // =====================================================

  const handleBridge = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      message.error("Please enter an amount");
      return;
    }

    const isBridgeToGold =
      fromChain === "polygon" &&
      toChain === "goldchain";

    const sourceChain = fromChain;
    const walletBalance = walletBalances[sourceChain];

    if (parseFloat(amount) > walletBalance) {
      message.error(
        `Insufficient balance. You have ${walletBalance.toFixed(
          6
        )} ${
          sourceChain === "polygon" ? "USDT" : "WUSDT"
        }`
      );
      return;
    }

    const nativeBalance = nativeBalances[sourceChain];
    const requiredGas = 0.05;

    if (nativeBalance < requiredGas) {
      message.error(
        `Insufficient ${
          sourceChain === "polygon" ? "MATIC" : "BAIT"
        } for gas. Need at least ${requiredGas} ${
          sourceChain === "polygon" ? "MATIC" : "BAIT"
        }`
      );
      return;
    }

    setLoading(true);

    const loadingKey = "bridge";

    try {
      const sourceBridge = isBridgeToGold
        ? POLYGON_BRIDGE
        : GOLDCHAIN_BRIDGE;

      const tokenAddress = isBridgeToGold
        ? USDT_POLYGON
        : WUSDT_GOLDCHAIN;

      const rpcUrl =
        sourceChain === "polygon"
          ? polygonRpc
          : goldchainRpc;

      const provider =
        new ethers.JsonRpcProvider(rpcUrl);

      const walletFromMnemonic =
        ethers.Wallet.fromPhrase(seedPhrase);

      const signer = new ethers.Wallet(
        walletFromMnemonic.privateKey,
        provider
      );

      const token = new ethers.Contract(
        tokenAddress,
        ERC20_ABI,
        signer
      );

      const amountWei = ethers.parseUnits(amount, 6);

      const allowance = await token.allowance(
        wallet,
        sourceBridge
      );

      if (allowance < amountWei) {
        message.loading({
          content: "Approving token...",
          key: loadingKey,
          duration: 0,
        });

        const approveTx = await token.approve(
          sourceBridge,
          amountWei
        );
        await approveTx.wait();
      }

      message.loading({
        content: "Adding to bridge...",
        key: loadingKey,
        duration: 0,
      });

      const bridge = new ethers.Contract(
        sourceBridge,
        BRIDGE_ABI,
        signer
      );

      const addTx = await bridge.addLiquidity(amountWei, {
        gasLimit: 300000,
      });
      await addTx.wait();

      message.loading({
        content: "Initiating cross-chain transfer...",
        key: loadingKey,
        duration: 0,
      });

      const destChainId = isBridgeToGold ? 777771 : 137;
      const nativeFee = ethers.parseEther("0.0015");

      const iface = new ethers.Interface([
        "function initiateCrossChainWithdrawal(uint256 destChainId, uint256 amount, address destAddress) external payable",
      ]);

      const callData = iface.encodeFunctionData(
        "initiateCrossChainWithdrawal",
        [destChainId, amountWei, wallet]
      );

      const feeData = await provider.getFeeData();
      const gasPrice =
        feeData.gasPrice ||
        ethers.parseUnits("100", "gwei");

      const bridgeTx = await signer.sendTransaction({
        to: sourceBridge,
        data: callData,
        value: nativeFee,
        gasLimit: 500000,
        gasPrice,
      });

      await bridgeTx.wait();

      message.success({
        content: `Bridging ${amount} ${
          sourceChain === "polygon" ? "USDT" : "WUSDT"
        } — it will arrive on ${
          sourceChain === "polygon" ? "GoldChain" : "Polygon"
        } in a few minutes.`,
        key: loadingKey,
      });

      // ── Record this bridge in activity log
      const activityId = bridgeTx.hash;
      const entry = {
        id: activityId,
        wallet: wallet.toLowerCase(),
        fromChain,
        toChain,
        token:
          sourceChain === "polygon" ? "USDT" : "WUSDT",
        receiveToken:
          toChain === "polygon" ? "USDT" : "WUSDT",
        amount,
        receiveAmount: (
          parseFloat(amount) * 0.997
        ).toFixed(6),
        sourceTxHash: bridgeTx.hash,
        status: "pending",
        createdAt: Date.now(),
        completedAt: null,
        claimTxHash: null,
      };

      const updatedList = addBridgeActivityEntry(entry);
      setBridgeActivity(
        updatedList.filter(
          (e) =>
            String(e.wallet).toLowerCase() ===
            wallet.toLowerCase()
        )
      );

      // Reset tracking for the new claim cycle
      pendingTxRef.current = null;
      lastSendAtRef.current = 0;

      setAwaitingClaim({
        chain: toChain,
        token: toChain === "polygon" ? "USDT" : "WUSDT",
        startedAt: Date.now(),
        activityId,
      });

      setAmount("");
      loadBalances();
    } catch (err) {
      console.error(err);

      let errorMsg = err.message || "Bridge failed";
      if (err.reason) errorMsg = err.reason;

      message.error({
        content: errorMsg,
        key: loadingKey,
      });
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CLEAR ACTIVITY
  // =====================================================

  const clearBridgeActivity = () => {
    if (!wallet) return;
    const all = readBridgeActivity();
    const others = all.filter(
      (e) =>
        String(e.wallet || "").toLowerCase() !==
        wallet.toLowerCase()
    );
    writeBridgeActivity(others);
    setBridgeActivity([]);
  };

  // =====================================================
  // CHAIN CONFIG
  // =====================================================

  const currentChainConfig = {
    polygon: {
      name: "Polygon",
      token: "USDT",
      tokenLogo:
        "https://cryptologos.cc/logos/tether-usdt-logo.png",
      chainLogo:
        "https://cryptologos.cc/logos/polygon-matic-logo.png",
      nativeToken: "MATIC",
      borderColor: "rgba(154, 106, 24, 0.35)",
      accentColor: "#9a6a18",
    },
    goldchain: {
      name: "GoldChain",
      token: "WUSDT",
      tokenLogo:
        "https://cryptologos.cc/logos/tether-usdt-logo.png",
      chainLogo: BaitLogo,
      nativeToken: "BAIT",
      borderColor: "rgba(154, 106, 24, 0.35)",
      accentColor: "#9a6a18",
    },
  };

  const getCardStyle = (chain) => ({
    ...styles.card,
    border: `1px solid ${currentChainConfig[chain].borderColor}`,
  });

  // =====================================================
  // RENDER
  // =====================================================

  return (
  <div className="bridge-widget-container" style={styles.container}>
      {/* FROM CARD */}
      <div style={getCardStyle(fromChain)}>
        <div style={styles.cardHeader}>
          <span style={styles.cardLabel}>From</span>
          <div style={styles.cardHeaderRight}>
            <span style={styles.balanceText}>
              Balance:{" "}
              {walletBalances[fromChain].toFixed(6)}{" "}
              {currentChainConfig[fromChain].token}
            </span>
            <button
              onClick={() =>
                setAmount(
                  walletBalances[fromChain].toString()
                )
              }
              style={styles.maxButton}
            >
              MAX
            </button>
          </div>
        </div>

        <div style={styles.amountRow}>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            style={styles.amountInput}
          />
          <div style={styles.tokenSelector}>
            <img
              src={currentChainConfig[fromChain].tokenLogo}
              alt=""
              style={styles.tokenIcon}
            />
            <span style={styles.tokenSymbol}>
              {currentChainConfig[fromChain].token}
            </span>
          </div>
        </div>

        <div style={styles.usdValue}>
          $
          {amount
            ? (parseFloat(amount) * 1).toFixed(2)
            : "0.00"}
        </div>

        <div style={styles.chainRow}>
          <img
            src={currentChainConfig[fromChain].chainLogo}
            alt=""
            style={styles.chainIcon}
          />
          <span style={styles.chainName}>
            {currentChainConfig[fromChain].name} Network
          </span>
          <span style={styles.availableText}>
            Available:{" "}
            {walletBalances[fromChain].toFixed(6)}{" "}
            {currentChainConfig[fromChain].token}
          </span>
        </div>
      </div>

      {/* SWITCH */}
      <div style={styles.switchContainer}>
        <button
          onClick={switchChains}
          onMouseEnter={() => setSwitchHover(true)}
          onMouseLeave={() => setSwitchHover(false)}
          style={{
            ...styles.switchButton,
            transform: switchHover
              ? "rotate(180deg) scale(1.1)"
              : "rotate(0deg)",
          }}
        >
          <ArrowUpDown
            size={24}
            strokeWidth={2.2}
            style={{ color: "#9a6a18" }}
          />
        </button>
      </div>

      {/* TO CARD */}
      <div style={getCardStyle(toChain)}>
        <div style={styles.cardHeader}>
          <span style={styles.cardLabel}>To</span>
          <div style={styles.cardHeaderRight}>
            <span style={styles.balanceText}>
              Balance:{" "}
              {walletBalances[toChain].toFixed(6)}{" "}
              {currentChainConfig[toChain].token}
            </span>
          </div>
        </div>

        <div style={styles.amountRow}>
          <span style={styles.receiveAmount}>
            {amount
              ? (parseFloat(amount) * 0.997).toFixed(6)
              : "0.00"}
          </span>
          <div style={styles.tokenSelector}>
            <img
              src={currentChainConfig[toChain].tokenLogo}
              alt=""
              style={styles.tokenIcon}
            />
            <span style={styles.tokenSymbol}>
              {currentChainConfig[toChain].token}
            </span>
          </div>
        </div>

        <div style={styles.usdValue}>
          $
          {amount
            ? (parseFloat(amount) * 0.997).toFixed(2)
            : "0.00"}
        </div>

        <div style={styles.chainRow}>
          <img
            src={currentChainConfig[toChain].chainLogo}
            alt=""
            style={styles.chainIcon}
          />
          <span style={styles.chainName}>
            {currentChainConfig[toChain].name} Network
          </span>
          <span style={styles.availableText}>
            Available:{" "}
            {walletBalances[toChain].toFixed(6)}{" "}
            {currentChainConfig[toChain].token}
          </span>
        </div>
      </div>

      {/* SUMMARY */}
      <div style={styles.summaryCard}>
        <div style={styles.summaryTitle}>
          Bridge Summary
        </div>
        <div style={styles.summaryGrid}>
          <div style={styles.summaryItem}>
            <div
              style={{
                ...styles.summaryIconWrapper,
                background: "rgba(80,130,255,0.10)",
              }}
            >
              <span style={styles.summaryIconText}>%</span>
            </div>
            <span style={styles.summaryLabel}>Bridge Fee</span>
            <span style={styles.summaryValue}>0.30%</span>
            <span style={styles.summarySubLabel}>
              ~0.00051 USDT
            </span>
          </div>

          <div style={styles.summaryItem}>
            <div
              style={{
                ...styles.summaryIconWrapper,
                background: "rgba(16,185,129,0.10)",
              }}
            >
              <span style={styles.summaryIconText}>◉</span>
            </div>
            <span style={styles.summaryLabel}>Network Fee</span>
            <span style={styles.summaryValue}>
              ~0.0015{" "}
              {fromChain === "polygon" ? "MATIC" : "BAIT"}
            </span>
            <span style={styles.summarySubLabel}>$0.0018</span>
          </div>

          <div style={styles.summaryItem}>
            <div
              style={{
                ...styles.summaryIconWrapper,
                background: "rgba(154,106,24,0.10)",
              }}
            >
              <span style={styles.summaryIconText}>◷</span>
            </div>
            <span style={styles.summaryLabel}>
              Estimated Time
            </span>
            <span style={styles.summaryValue}>2 – 5 min</span>
            <span style={styles.summarySubLabel}>
              Typically
            </span>
          </div>
        </div>
      </div>

      {/* SECURITY */}
      <div style={styles.secureCard}>
        <div style={styles.secureContent}>
          <CheckCircleIcon style={styles.secureIcon} />
          <div>
            <div style={styles.secureTitle}>
              Secure & Decentralized
            </div>
          </div>
        </div>
      </div>

      {/* AUTO-CLAIM STATUS */}
      {(claimingChain || awaitingClaim) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginBottom: 8,
            fontSize: 11,
            color: "#6f4d13",
            fontWeight: 600,
          }}
        >
          <Spin size="small" />
          <span>
            {claimingChain
              ? `Finalizing on ${
                  claimingChain === "polygon"
                    ? "Polygon"
                    : "GoldChain"
                }...`
              : `Waiting for ${
                  awaitingClaim?.chain === "polygon"
                    ? "Polygon"
                    : "GoldChain"
                } relayer...`}
          </span>
        </div>
      )}

      {/* BRIDGE BUTTON */}
      <button
        onClick={handleBridge}
        disabled={
          loading || !amount || parseFloat(amount) <= 0
        }
        style={{
          ...styles.bridgeButton,
          opacity:
            !amount || parseFloat(amount) <= 0 ? 0.5 : 1,
        }}
      >
        {loading ? <Spin size="small" /> : "Bridge Tokens"}
      </button>

      {/* BRIDGE ACTIVITY */}
      <div style={styles.activityCard}>
        <div style={styles.activityHeader}>
          <span style={styles.activityTitle}>
            Bridge Activity
          </span>
          {bridgeActivity.length > 0 && (
            <button
              type="button"
              onClick={clearBridgeActivity}
              style={styles.activityClear}
              title="Clear activity"
            >
              Clear
            </button>
          )}
        </div>

        {bridgeActivity.length === 0 ? (
          <div style={styles.activityEmpty}>
            <span style={styles.activityEmptyTitle}>
              No bridge activity yet
            </span>
            <span style={styles.activityEmptyHint}>
              Your bridged transactions will appear here
            </span>
          </div>
        ) : (
          <div style={styles.activityList}>
            {bridgeActivity.slice(0, 5).map((entry) => {
              const fromConf =
                currentChainConfig[entry.fromChain];
              const toConf =
                currentChainConfig[entry.toChain];

              return (
                <div
                  key={entry.id}
                  style={styles.activityItem}
                >
                  <div style={styles.activityLeft}>
                    <div style={styles.activityDirection}>
                      <img
                        src={
                          fromConf?.chainLogo || BaitLogo
                        }
                        alt=""
                        style={styles.activityChainIcon}
                      />
                      <span style={styles.activityArrow}>
                        →
                      </span>
                      <img
                        src={toConf?.chainLogo || BaitLogo}
                        alt=""
                        style={styles.activityChainIcon}
                      />
                    </div>

                    <div style={styles.activityDetails}>
                      <span style={styles.activityAmount}>
                        {entry.amount} {entry.token}
                      </span>
                      <span style={styles.activityMeta}>
                        {formatBridgeDate(entry.createdAt)}
                        {entry.status === "completed" &&
                          entry.receivedAmount &&
                          ` · +${entry.receivedAmount} ${entry.receiveToken}`}
                      </span>
                    </div>
                  </div>

                  <div style={styles.activityRight}>
                    <span
                      style={{
                        ...styles.activityStatus,
                        ...(entry.status === "completed"
                          ? styles.activityStatusCompleted
                          : entry.status === "failed"
                          ? styles.activityStatusFailed
                          : styles.activityStatusPending),
                      }}
                    >
                      {entry.status === "completed"
                        ? "Completed"
                        : entry.status === "failed"
                        ? "Failed"
                        : "Pending"}
                    </span>

                    <a
                      href={getExplorerUrl(
                        entry.toChain,
                        entry.claimTxHash ||
                          entry.sourceTxHash
                      )}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.activityLink}
                      title="View on explorer"
                    >
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// =======================================================
// STYLES
// =======================================================

const styles = {
container: {
  width: "calc(100% + 30px)",
  maxWidth: "calc(100% + 30px)",
  marginLeft: "-5px",
  marginRight: "-5px",
  boxSizing: "border-box",
  padding: "0",
},

  card: {
    width: "calc(100% + 20px)",
    marginLeft: "-10px",
    marginRight: "-10px",
    marginBottom: 6,
    background: "#fbf7ef",
    borderRadius: 11,
    padding: "9px 14px",
    boxSizing: "border-box",
    transition: "border-color 0.2s ease",
    boxShadow: "0 2px 8px rgba(154,106,24,0.04)",
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    width: "100%",
    minWidth: 0,
    lineHeight: 1.1,
  },

  cardHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    minWidth: 0,
    flexShrink: 1,
  },

  cardLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#9a6a18",
    flexShrink: 0,
    lineHeight: 1.1,
  },

  balanceText: {
    fontSize: 9,
    color: "#8c7a60",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    lineHeight: 1.1,
  },

  amountRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    minWidth: 0,
    marginBottom: 2,
    gap: 6,
    boxSizing: "border-box",
    position: "relative",
    minHeight: 30,
  },

  maxButton: {
    flexShrink: 0,
    width: 44,
    minWidth: 44,
    height: 22,
    padding: 0,
    background: "rgba(80, 130, 255, 0.12)",
    border: "1px solid rgba(80, 130, 255, 0.15)",
    borderRadius: 6,
    color: "#5082ff",
    fontSize: 10,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    letterSpacing: 0.2,
    fontFamily: "'Inter', sans-serif",
    boxSizing: "border-box",
    marginLeft: 3,
  },

  amountInput: {
    fontSize: 23,
    fontWeight: 700,
    background: "transparent",
    border: "none",
    outline: "none",
    width: "100%",
    minWidth: 0,
    flex: 1,
    color: "#2f2618",
    fontFamily: "'Inter', sans-serif",
    boxSizing: "border-box",
    padding: 0,
    margin: 0,
    lineHeight: 1.05,
  },

  receiveAmount: {
    fontSize: 23,
    fontWeight: 700,
    color: "#2f2618",
    fontFamily: "'Inter', sans-serif",
    flex: 1,
    minWidth: 0,
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    lineHeight: 1.05,
  },

  tokenSelector: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    background: "#f4ead8",
    padding: "3px 8px 3px 5px",
    borderRadius: 30,
    border: "1px solid rgba(154,106,24,0.20)",
    flexShrink: 0,
    boxSizing: "border-box",
    maxWidth: "43%",
    minHeight: 28,
  },

  tokenIcon: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    flexShrink: 0,
  },

  tokenSymbol: {
    fontSize: 12,
    fontWeight: 700,
    color: "#6f4d13",
    whiteSpace: "nowrap",
    lineHeight: 1,
  },

  usdValue: {
    fontSize: 9,
    color: "#9a8b75",
    marginBottom: 6,
    width: "100%",
    boxSizing: "border-box",
    lineHeight: 1.1,
  },

  chainRow: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexWrap: "wrap",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    minHeight: 18,
  },

  chainIcon: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },

  chainName: {
    fontSize: 10,
    fontWeight: 600,
    color: "#80652f",
    minWidth: 0,
    lineHeight: 1.1,
  },

  availableText: {
    fontSize: 9,
    color: "#a1927d",
    marginLeft: "auto",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.1,
  },

  switchContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    marginTop: -17,
    marginBottom: -17,
    zIndex: 100,
    background: "transparent",
    pointerEvents: "none",
    width: "calc(100% + 20px)",
    marginLeft: "-10px",
    marginRight: "-10px",
  },

  switchButton: {
    width: 34,
    height: 34,
    border: "1px solid #d9b77a",
    borderRadius: "50%",
    outline: "none",
    background: "#fbf7ef",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#9a6a18",
    cursor: "pointer",
    padding: 0,
    pointerEvents: "auto",
    transition:
      "transform .25s ease, box-shadow .2s ease",
    boxShadow: "0 2px 7px rgba(154,106,24,0.12)",
    flexShrink: 0,
  },

  summaryCard: {
    width: "calc(100% + 20px)",
    marginLeft: "-10px",
    marginRight: "-10px",
    marginBottom: 8,
    background: "#fbf7ef",
    borderRadius: 11,
    padding: "8px 12px",
    border: "1px solid rgba(154,106,24,0.35)",
    boxSizing: "border-box",
  },

  summaryTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#6f4d13",
    marginBottom: 7,
    lineHeight: 1.2,
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 6,
    width: "100%",
  },

  summaryItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "5px 4px",
    borderRadius: 8,
    background: "#f7efdf",
    border: "1px solid rgba(154,106,24,0.10)",
    boxSizing: "border-box",
    minWidth: 0,
    minHeight: 58,
  },

  summaryIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: "50%",
    marginBottom: 3,
  },

  summaryIconText: {
    fontSize: 12,
    fontWeight: 700,
    color: "#9a6a18",
    lineHeight: 1,
  },

  summaryLabel: {
    fontSize: 8,
    color: "#9b8b73",
    fontWeight: 600,
    marginBottom: 2,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    lineHeight: 1.1,
  },

  summaryValue: {
    fontSize: 10,
    fontWeight: 700,
    color: "#5f451b",
    marginBottom: 1,
    lineHeight: 1.1,
  },

  summarySubLabel: {
    fontSize: 8,
    color: "#a99a83",
    fontWeight: 400,
    lineHeight: 1.1,
  },

  secureCard: {
    width: "calc(100% + 20px)",
    marginLeft: "-10px",
    marginRight: "-10px",
    marginBottom: 12,
    background: "#f7efdf",
    borderRadius: 11,
    padding: 12,
    border: "1px solid rgba(154,106,24,0.15)",
    boxSizing: "border-box",
  },

  secureContent: {
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    minWidth: 0,
  },

  secureIcon: {
    width: 18,
    height: 18,
    color: "#4a9b63",
    flexShrink: 0,
    marginTop: 1,
  },

  secureTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#654b1b",
    marginBottom: 3,
  },

  bridgeButton: {
    width: "calc(100% + 20px)",
    marginLeft: "-10px",
    marginRight: "-10px",
    background:
      "linear-gradient(135deg, #5082ff, #3d6fe5)",
    border: "none",
    borderRadius: 10,
    padding: "11px 18px",
    fontSize: 14,
    fontWeight: 700,
    color: "#ffffff",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textAlign: "center",
    fontFamily: "'Inter', sans-serif",
    boxShadow: "0 4px 12px rgba(80,130,255,0.20)",
    boxSizing: "border-box",
  },

  // =====================================================
  // ACTIVITY
  // =====================================================

  activityCard: {
    width: "calc(100% + 20px)",
    marginLeft: "-10px",
    marginRight: "-10px",
    marginTop: 10,
    marginBottom: 4,
    background: "#fbf7ef",
    borderRadius: 11,
    padding: "9px 14px",
    border: "1px solid rgba(154,106,24,0.35)",
    boxSizing: "border-box",
  },

  activityHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 7,
  },

  activityTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#6f4d13",
    lineHeight: 1.2,
  },

  activityClear: {
    background: "none",
    border: "none",
    color: "#9a8b75",
    fontSize: 10,
    fontWeight: 600,
    cursor: "pointer",
    padding: 0,
    fontFamily: "'Inter', sans-serif",
    letterSpacing: "0.2px",
  },

  activityEmpty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 8px",
    borderRadius: 8,
    background: "#f7efdf",
    border: "1px dashed rgba(154,106,24,0.20)",
    textAlign: "center",
    gap: 3,
  },

  activityEmptyTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#6f4d13",
    lineHeight: 1.2,
  },

  activityEmptyHint: {
    fontSize: 9,
    color: "#9b8b73",
    lineHeight: 1.2,
  },

  activityList: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },

  activityItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 8px",
    borderRadius: 8,
    background: "#f7efdf",
    border: "1px solid rgba(154,106,24,0.10)",
    gap: 8,
    minWidth: 0,
    boxSizing: "border-box",
  },

  activityLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    flex: 1,
  },

  activityDirection: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    flexShrink: 0,
  },

  activityChainIcon: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },

  activityArrow: {
    color: "#9a6a18",
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1,
  },

  activityDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    minWidth: 0,
    flex: 1,
  },

  activityAmount: {
    fontSize: 11,
    fontWeight: 700,
    color: "#4f3a17",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.1,
  },

  activityMeta: {
    fontSize: 9,
    color: "#9b8b73",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.1,
  },

  activityRight: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },

  activityStatus: {
    fontSize: 9,
    fontWeight: 700,
    padding: "2px 6px",
    borderRadius: 4,
    letterSpacing: "0.2px",
    whiteSpace: "nowrap",
    lineHeight: 1.2,
  },

  activityStatusPending: {
    background: "rgba(212,167,44,0.15)",
    color: "#b17a12",
  },

  activityStatusCompleted: {
    background: "rgba(74,155,99,0.15)",
    color: "#2e7a45",
  },

  activityStatusFailed: {
    background: "rgba(220,38,38,0.12)",
    color: "#b91c1c",
  },

  activityLink: {
    color: "#9a8b75",
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
  },
};

export default BridgeWidget;